import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { QuoteIcon, AwardIcon, BriefcaseIcon, UsersIcon, TrendingUpIcon } from 'lucide-react';
const achievements = [{
  icon: <BriefcaseIcon className="w-5 h-5" data-id="element-111" />,
  value: '15+',
  label: 'років у бізнесі'
}, {
  icon: <UsersIcon className="w-5 h-5" data-id="element-112" />,
  value: '500+',
  label: 'закритих вакансій'
}, {
  icon: <TrendingUpIcon className="w-5 h-5" data-id="element-113" />,
  value: '300+',
  label: 'компаній‑клієнтів'
}, {
  icon: <AwardIcon className="w-5 h-5" data-id="element-114" />,
  value: 'Top‑10',
  label: 'HR‑агентств України'
}];
export function CeoSection() {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <section className="py-20 lg:py-28 bg-white" ref={ref} data-id="element-115">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-116">
        <div className="grid lg:grid-cols-5 gap-10 lg:gap-16 items-center" data-id="element-117">
          {/* Photo / Avatar side */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.6
        }} className="lg:col-span-2" data-id="element-118">
            <div className="relative max-w-sm mx-auto lg:mx-0" data-id="element-119">
              {/* Main avatar */}
              <div className="aspect-[4/5] rounded-3xl bg-gradient-to-br from-navy-800 via-navy-900 to-navy-800 overflow-hidden relative" data-id="element-120">
                {/* Abstract pattern overlay */}
                <div className="absolute inset-0" data-id="element-121">
                  <div className="absolute top-8 right-8 w-32 h-32 rounded-full bg-primary-500/10 blur-2xl" data-id="element-122" />
                  <div className="absolute bottom-12 left-6 w-24 h-24 rounded-full bg-primary-400/10 blur-2xl" data-id="element-123" />
                  <div className="absolute inset-0 opacity-[0.04]" style={{
                  backgroundImage: `linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)`,
                  backgroundSize: '40px 40px'
                }} data-id="element-124" />
                </div>
                {/* Avatar placeholder */}
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-8" data-id="element-125">
                  <div className="w-28 h-28 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center mb-5 shadow-xl shadow-primary-500/20" data-id="element-126">
                    <span className="text-white font-extrabold text-3xl" data-id="element-127">
                      СД
                    </span>
                  </div>
                  <h3 className="text-xl font-extrabold text-white" data-id="element-128">
                    Сергій Денисюк
                  </h3>
                  <p className="text-primary-300 text-sm font-medium mt-1" data-id="element-129">
                    CEO & Founder
                  </p>
                  <p className="text-navy-400 text-xs mt-0.5" data-id="element-130">
                    ESCA — Executive Search Consulting Agency
                  </p>
                </div>
              </div>

              {/* Floating badge */}
              <motion.div initial={{
              opacity: 0,
              scale: 0.8
            }} animate={isInView ? {
              opacity: 1,
              scale: 1
            } : {}} transition={{
              duration: 0.5,
              delay: 0.4
            }} className="absolute -bottom-4 -right-4 bg-white rounded-2xl shadow-xl border border-navy-100 p-4" data-id="element-131">
                <div className="text-2xl font-extrabold text-primary-600" data-id="element-132">
                  15+
                </div>
                <div className="text-xs text-navy-500 font-medium" data-id="element-133">
                  років досвіду
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Content side */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="lg:col-span-3" data-id="element-134">
            <span className="text-sm font-semibold text-primary-600 tracking-wide uppercase" data-id="element-135">
              Засновник агентства
            </span>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-navy-900 tracking-tight" data-id="element-136">
              Сергій Денисюк
            </h2>
            <p className="mt-2 text-lg text-navy-400 font-medium" data-id="element-137">
              CEO & Founder ESCA
            </p>

            {/* Quote */}
            <div className="mt-8 relative" data-id="element-138">
              <QuoteIcon className="w-10 h-10 text-primary-200 mb-3" data-id="element-139" />
              <blockquote className="text-xl sm:text-2xl text-navy-700 leading-relaxed font-medium italic" data-id="element-140">
                «Ми знаємо все про болі компаній по формуванню кадрового складу
                будучи безпосередньо у реальному операційному бізнесі. Наше
                завдання — швидко та якісно вирішити ваше питання та знайти
                спеціаліста максимально відповідного для вашої вакансії!»
              </blockquote>
            </div>

            {/* Expertise points */}
            <div className="mt-10 space-y-4" data-id="element-141">
              <div className="flex items-start gap-3" data-id="element-142">
                <div className="w-6 h-6 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-143">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} data-id="element-144">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-145" />
                  </svg>
                </div>
                <p className="text-navy-600 leading-relaxed" data-id="element-146">
                  <strong className="text-navy-900" data-id="element-147">
                    Реальний бізнес‑досвід.
                  </strong>{' '}
                  Розуміння потреб компаній зсередини — не теоретично, а з
                  практики управління операційним бізнесом.
                </p>
              </div>
              <div className="flex items-start gap-3" data-id="element-148">
                <div className="w-6 h-6 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-149">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} data-id="element-150">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-151" />
                  </svg>
                </div>
                <p className="text-navy-600 leading-relaxed" data-id="element-152">
                  <strong className="text-navy-900" data-id="element-153">
                    Executive Search експертиза.
                  </strong>{' '}
                  Спеціалізація на пошуку C‑level та ключових спеціалістів для
                  бізнесу різних масштабів.
                </p>
              </div>
              <div className="flex items-start gap-3" data-id="element-154">
                <div className="w-6 h-6 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-155">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} data-id="element-156">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-157" />
                  </svg>
                </div>
                <p className="text-navy-600 leading-relaxed" data-id="element-158">
                  <strong className="text-navy-900" data-id="element-159">
                    Персональна залученість.
                  </strong>{' '}
                  Особисто контролює ключові проєкти та гарантує якість підбору
                  на кожному етапі.
                </p>
              </div>
            </div>

            {/* Achievement stats */}
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4" data-id="element-160">
              {achievements.map((item, index) => <motion.div key={item.label} initial={{
              opacity: 0,
              y: 16
            }} animate={isInView ? {
              opacity: 1,
              y: 0
            } : {}} transition={{
              duration: 0.4,
              delay: 0.3 + index * 0.08
            }} className="p-4 rounded-xl bg-navy-50/60 border border-navy-100 text-center" data-id="element-161">
                  <div className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-primary-100 text-primary-600 mb-2" data-id="element-162">
                    {item.icon}
                  </div>
                  <div className="text-xl font-extrabold text-navy-900" data-id="element-163">
                    {item.value}
                  </div>
                  <div className="text-xs text-navy-500 mt-0.5" data-id="element-164">
                    {item.label}
                  </div>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}